import React from 'react';
import { Badge } from './ui/badge';
import { usePQC } from '../hooks/usePQC';
import { 
  Shield, 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Clock,
  Key,
  Lock,
  Unlock,
  Zap,
  Info
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface PQCStatusIndicatorProps {
  variant?: 'compact' | 'detailed' | 'badge-only';
  showTooltip?: boolean;
  className?: string;
}

const PQCStatusIndicator: React.FC<PQCStatusIndicatorProps> = ({ 
  variant = 'compact', 
  showTooltip = true,
  className = '' 
}) => {
  const {
    hasValidKeys,
    securityStatus,
    getKeyMetrics,
    isInitialized
  } = usePQC();

  const keyMetrics = getKeyMetrics();
  const hasKeys = hasValidKeys();
  
  // LOGS DE DEBUG PARA IDENTIFICAR PROBLEMA DE INICIALIZAÇÃO
  console.log('🔍 PQCStatusIndicator - Estado atual:', {
    isInitialized,
    hasKeys,
    securityStatus: securityStatus.level,
    keyMetrics: {
      totalKeys: keyMetrics.totalKeys,
      algorithms: keyMetrics.algorithms
    }
  });

  const getStatusConfig = () => {
    if (!isInitialized) {
      return {
        level: 'loading',
        color: 'bg-gray-100 text-gray-600 border-gray-200',
        icon: <Clock className="h-4 w-4" />,
        label: 'Carregando...',
        description: 'Inicializando sistema PQC'
      };
    }

    if (!hasKeys) {
      return {
        level: 'no-keys',
        color: 'bg-red-100 text-red-700 border-red-200',
        icon: <XCircle className="h-4 w-4" />,
        label: 'Não Protegido',
        description: 'Chaves PQC não configuradas'
      };
    }

    switch (securityStatus.level) {
      case 'high':
        return {
          level: 'high',
          color: 'bg-green-100 text-green-700 border-green-200',
          icon: <CheckCircle className="h-4 w-4" />,
          label: 'Protegido',
          description: 'Segurança pós-quântica ativa'
        };
      case 'medium':
        return {
          level: 'medium',
          color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
          icon: <AlertTriangle className="h-4 w-4" />,
          label: 'Atenção',
          description: 'Algumas recomendações pendentes'
        };
      case 'low':
        return {
          level: 'low',
          color: 'bg-orange-100 text-orange-700 border-orange-200',
          icon: <Clock className="h-4 w-4" />,
          label: 'Baixa',
          description: 'Chaves precisam de atenção'
        };
      case 'critical':
        return {
          level: 'critical',
          color: 'bg-red-100 text-red-700 border-red-200',
          icon: <XCircle className="h-4 w-4" />,
          label: 'Crítico',
          description: 'Ação imediata necessária'
        };
      default:
        return {
          level: 'unknown',
          color: 'bg-gray-100 text-gray-600 border-gray-200',
          icon: <Shield className="h-4 w-4" />,
          label: 'Desconhecido',
          description: 'Status não determinado'
        };
    }
  };

  const statusConfig = getStatusConfig();

  const getProtectionLevel = () => {
    if (!hasKeys) return 'Sem Proteção';
    
    const kyberLevel = keyMetrics.algorithms?.kyber || 'Kyber-768';
    const dilithiumLevel = keyMetrics.algorithms?.dilithium || 'Dilithium3';
    
    const levels: Record<string, number> = {
      'Kyber-512': 1, 'Kyber-768': 3, 'Kyber-1024': 5,
      'Dilithium2': 2, 'Dilithium3': 3, 'Dilithium5': 5
    };
    
    const maxLevel = Math.max(levels[kyberLevel] || 3, levels[dilithiumLevel] || 3);
    return `NIST Nível ${maxLevel}`;
  };

  const renderBadgeOnly = () => (
    <Badge 
      className={`${statusConfig.color} border ${className}`}
      title={showTooltip ? statusConfig.description : undefined}
    >
      <div className="flex items-center gap-1">
        {statusConfig.icon}
        <span className="text-xs font-medium">{statusConfig.label}</span>
      </div>
    </Badge>
  );

  const renderCompact = () => (
    <div 
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${statusConfig.color} ${className}`}
      title={showTooltip ? statusConfig.description : undefined}
    >
      {statusConfig.icon}
      <div className="flex flex-col">
        <span className="text-xs font-medium">{statusConfig.label}</span>
        <span className="text-xs opacity-75">{getProtectionLevel()}</span>
      </div>
    </div>
  );

  const renderDetailed = () => (
    <div className={`p-4 rounded-lg border ${statusConfig.color} ${className}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {statusConfig.icon}
          <div>
            <h4 className="font-medium text-sm">{statusConfig.label}</h4>
            <p className="text-xs opacity-75">{statusConfig.description}</p>
          </div>
        </div>
        <Badge variant="outline" className="text-xs">
          PQC
        </Badge>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="opacity-75">Nível de Proteção:</span>
          <span className="font-medium">{getProtectionLevel()}</span>
        </div>
        
        {hasKeys && (
          <>
            <div className="flex items-center justify-between text-xs">
              <span className="opacity-75">Chaves Ativas:</span>
              <span className="font-medium">{keyMetrics.totalKeys}</span>
            </div>
            
            {keyMetrics.lastRotation && (
              <div className="flex items-center justify-between text-xs">
                <span className="opacity-75">Última Rotação:</span>
                <span className="font-medium">
                  {formatDistanceToNow(new Date(keyMetrics.lastRotation), { 
                    addSuffix: true, 
                    locale: ptBR 
                  })}
                </span>
              </div>
            )}
            
            <div className="flex items-center justify-between text-xs">
              <span className="opacity-75">Operações Hoje:</span>
              <span className="font-medium">{keyMetrics.operationsToday}</span>
            </div>
          </>
        )}
        
        <div className="flex items-center justify-between text-xs">
          <span className="opacity-75">Última Verificação:</span>
          <span className="font-medium">
            {formatDistanceToNow(new Date(securityStatus.lastCheck), { 
              addSuffix: true, 
              locale: ptBR 
            })}
          </span>
        </div>
      </div>
      
      {hasKeys && (
        <div className="mt-3 pt-3 border-t border-current border-opacity-20">
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <Key className="h-3 w-3" />
              <span>Kyber</span>
            </div>
            <div className="flex items-center gap-1">
              <Lock className="h-3 w-3" />
              <span>Dilithium</span>
            </div>
            <div className="flex items-center gap-1">
              <Zap className="h-3 w-3" />
              <span>Ativo</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  switch (variant) {
    case 'badge-only':
      return renderBadgeOnly();
    case 'detailed':
      return renderDetailed();
    case 'compact':
    default:
      return renderCompact();
  }
};

export default PQCStatusIndicator;